import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react'

// Eager — homepage and route guards (loaded on first paint)
import ProtectedAdminRoute  from './components/ProtectedAdminRoute.jsx';
import ProtectedClientRoute from './components/ProtectedClientRoute.jsx';

// Lazy — all other routes (loaded on demand)
const LandingV5           = lazy(() => import('./pages/LandingV5.jsx'));
const LandingV6           = lazy(() => import('./pages/LandingV6.jsx'));
const LandingV7           = lazy(() => import('./pages/LandingV7.jsx'));
const LandingV8           = lazy(() => import('./pages/LandingV8.jsx'));
const Dashboard           = lazy(() => import('./pages/Dashboard.jsx'));
const AdminLogin          = lazy(() => import('./pages/AdminLogin.jsx'));
const ResetPassword       = lazy(() => import('./pages/ResetPassword.jsx'));
const AdminSiteEditor     = lazy(() => import('./pages/AdminSiteEditor.jsx'));
const ClientSite          = lazy(() => import('./pages/ClientSite.jsx'));
const ArticlePage         = lazy(() => import('./pages/ArticlePage.jsx'));
const BlogPostPage        = lazy(() => import('./pages/BlogPostPage.jsx'));
const VoicePostPage       = lazy(() => import('./pages/VoicePostPage.jsx'));
const ClientLogin         = lazy(() => import('./pages/ClientLogin.jsx'));
const ClientDashboardV2   = lazy(() => import('./components/ClientDashboardV2'));
const ClientDashboardV3   = lazy(() => import('./components/ClientDashboardV3'));
const AuthCallback        = lazy(() => import('./pages/AuthCallback.jsx'));
const OnboardingSuccess   = lazy(() => import('./pages/OnboardingSuccess.jsx'));
const Upgrade             = lazy(() => import('./pages/Upgrade.jsx'));
const GapEngine           = lazy(() => import('./pages/GapEngine.jsx'));
const AdminUsers          = lazy(() => import('./pages/AdminUsers.jsx'));
const TermsOfService      = lazy(() => import('./pages/legal/TermsOfService.jsx'));
const PrivacyPolicy       = lazy(() => import('./pages/legal/PrivacyPolicy.jsx'));
const CancellationPolicy  = lazy(() => import('./pages/legal/CancellationPolicy.jsx'));
const I99BlogV2           = lazy(() => import('./pages/I99BlogV2.jsx'));
const I99BlogCategoryV2   = lazy(() => import('./pages/I99BlogCategoryV2.jsx'));
const I99BlogPostV2       = lazy(() => import('./pages/I99BlogPostV2.jsx'));

/**
 * Catches Supabase auth errors that land at the site root as a hash fragment,
 * e.g. https://i99.vercel.app/#error=access_denied&error_code=otp_expired
 * Redirects to the client login page with an error notice.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AuthErrorRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (location.pathname !== '/') return;
    const hash = window.location.hash;
    if (!hash.includes('error=')) return;
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const code   = params.get('error_code') || params.get('error') || 'unknown';
    navigate(`/client/login?auth_error=${encodeURIComponent(code)}`, { replace: true });
  }, [location.pathname, navigate]);
  return null;
}

export default function App() {
  return (
    <>
      <BrowserRouter>
        <ScrollToTop />
        <AuthErrorRedirect />
        <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
        <Routes>
          {/* ── Public ──────────────────────────────────────────── */}
          <Route path="/"                element={<LandingV8 />} />
          <Route path="/v5"              element={<LandingV5 />} />
          <Route path="/v6"              element={<LandingV6 />} />
          <Route path="/v7"              element={<LandingV7 />} />
          <Route path="/onboarding/success" element={<OnboardingSuccess />} />
          <Route path="/upgrade"            element={<Upgrade />} />
          {/* Public client minisites and article pages */}
          <Route path="/site/:businessSlug"                       element={<ClientSite />} />
          <Route path="/site/:businessSlug/articles/:articleSlug" element={<ArticlePage />} />
          <Route path="/site/:businessSlug/blog/:postId"          element={<BlogPostPage />} />
          <Route path="/site/:businessSlug/voice/:postIdOrSlug"   element={<VoicePostPage />} />
          {/* ── i99 Blog ────────────────────────────────────────── */}
          <Route path="/blog"                          element={<I99BlogV2 />} />
          <Route path="/blog/category/:categorySlug"  element={<I99BlogCategoryV2 />} />
          <Route path="/blog/:slug"                    element={<I99BlogPostV2 />} />
          {/* ── Legal ───────────────────────────────────────────── */}
          <Route path="/legal/terms"        element={<TermsOfService />} />
          <Route path="/legal/privacy"      element={<PrivacyPolicy />} />
          <Route path="/legal/cancellation" element={<CancellationPolicy />} />
          {/* ── Auth ────────────────────────────────────────────── */}
          <Route path="/admin/login"    element={<AdminLogin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/client/login"   element={<ClientLogin />} />
          <Route path="/auth/callback"  element={<AuthCallback />} />
          {/* ── Protected admin ─────────────────────────────────── */}
          <Route path="/dashboard" element={
            <ProtectedAdminRoute><Dashboard /></ProtectedAdminRoute>
          } />
          <Route path="/admin/edit/:businessSlug" element={
            <ProtectedAdminRoute><AdminSiteEditor /></ProtectedAdminRoute>
          } />
          <Route path="/admin/gap-engine" element={
            <ProtectedAdminRoute><GapEngine /></ProtectedAdminRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedAdminRoute><AdminUsers /></ProtectedAdminRoute>
          } />
          {/* ── Protected client ────────────────────────────────── */}
          <Route path="/client/dashboard" element={
            <ProtectedClientRoute><ClientDashboardV3 /></ProtectedClientRoute>
          } />
          <Route path="/client/dashboard-v2" element={
            <ProtectedClientRoute><ClientDashboardV2 /></ProtectedClientRoute>
          } />
          {/* Legacy token URLs → redirect to login */}
          <Route path="/client/:token" element={<Navigate to="/client/login" replace />} />
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Analytics />
    </>
  );
}










